import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { Navigate, useNavigate, Link, useLocation } from 'react-router-dom';
import ProjectList from './components/ProjectList';
import ProjectWorkspace from './components/ProjectWorkspace';
import PromptComposer from './components/PromptComposer';
import StackSelector from './components/StackSelector';
import AdminLayout from './components/AdminLayout';
import { logout, isAuthenticated, authFetch } from './services/api';
import { settingsService, stackFromPreference } from './services/settingsService';
import { useTheme } from './hooks/useTheme';
import { resolveTemplateType, hasTemplateFiles } from './templates';
import { seedProjectTemplate } from './utils/seedProjectTemplate';
import { useToast } from './hooks/useToast';
import UpgradeModal from './components/UpgradeModal';
import InvitesBell from './components/collab/InvitesBell';
import Onboarding, { HelpButton, TourController } from './components/Onboarding';
import useOnboarding from './hooks/useOnboarding';

const MAIN_TABS = new Set(['home', 'projects', 'templates', 'settings']);

const NAV_ITEMS = [
  { id: 'home', label: 'Home' },
  { id: 'projects', label: 'My Projects' },
  { id: 'templates', label: 'Templates' },
  { id: 'settings', label: 'Settings' },
];

function normalizeView(saved) {
  if (saved === 'workspace') return 'workspace';
  if (MAIN_TABS.has(saved)) return saved;
  if (saved === 'projects') return 'projects';
  return 'home';
}

export default function AppShell() {
  // Clear any stale tour state from previous sessions
  sessionStorage.removeItem('tour_active');
  sessionStorage.removeItem('tour_step');

  const navigate = useNavigate();
  const location = useLocation();
  const { theme, mode, toggle, setMode } = useTheme();
  const [authed, setAuthed] = useState(isAuthenticated());
  const toast = useToast();
  const [view, setView] = useState(() => normalizeView(sessionStorage.getItem('app_view')));
  const [activeProjectId, setActiveProjectId] = useState(() => {
    return sessionStorage.getItem('app_active_project') || null;
  });
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [newProjectMode, setNewProjectMode] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [newProjectFramework, setNewProjectFramework] = useState('react-only');
  // ONE-SCREEN-CREATE: the composer's attachments (hero video, strict
  // scope) travel with the create request; `pendingAction` is a tool
  // that needs a live project (import / design reference) and so runs
  // in the workspace immediately after it opens.
  const [newProjectOptions, setNewProjectOptions] = useState({});
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingPrompt, setPendingPrompt] = useState(null);
  const [newProjectType, setNewProjectType] = useState('web');
  const [defaultStack, setDefaultStack] = useState('react-only');
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [userType, setUserType] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const onboarding = useOnboarding();
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(0);

  const activeTab = MAIN_TABS.has(view) ? view : 'home';
  const navRef = useRef(null);
  const tabRefs = useRef({});
  const [navIndicator, setNavIndicator] = useState({ left: 0, width: 0, opacity: 0 });

  const updateNavIndicator = useCallback(() => {
    const btn = tabRefs.current[activeTab];
    const nav = navRef.current;
    if (!btn || !nav) return;
    const navRect = nav.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    if (btnRect.width <= 0) return;
    setNavIndicator({
      left: btnRect.left - navRect.left,
      width: btnRect.width,
      opacity: 1,
    });
  }, [activeTab]);

  useLayoutEffect(() => {
    if (!projectsLoaded) return undefined;

    updateNavIndicator();
    const raf1 = requestAnimationFrame(() => {
      updateNavIndicator();
      requestAnimationFrame(updateNavIndicator);
    });

    const nav = navRef.current;
    let observer;
    if (nav && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => updateNavIndicator());
      observer.observe(nav);
    }

    return () => {
      cancelAnimationFrame(raf1);
      observer?.disconnect();
    };
  }, [updateNavIndicator, projectsLoaded]);

  useEffect(() => {
    window.addEventListener('resize', updateNavIndicator);
    return () => window.removeEventListener('resize', updateNavIndicator);
  }, [updateNavIndicator]);

  useEffect(() => {
    if (view === 'workspace') {
      sessionStorage.setItem('app_view', 'workspace');
    } else if (MAIN_TABS.has(view)) {
      sessionStorage.setItem('app_view', view);
    }
    if (activeProjectId) sessionStorage.setItem('app_active_project', activeProjectId);
    else sessionStorage.removeItem('app_active_project');
  }, [view, activeProjectId]);

  useEffect(() => {
    // Handle /admin/* URL paths
    if (location.pathname.startsWith('/admin')) {
      setView('settings');
      return undefined;
    }
    if (!location.state?.resetHome) return undefined;

    setView('home');
    setActiveProjectId(null);
    setActiveTemplate(null);
    setNewProjectMode(false);
    setNewProjectName('');
    setNewProjectDesc('');
    sessionStorage.setItem('app_view', 'home');
    sessionStorage.removeItem('app_active_project');
    navigate('/app', { replace: true, state: null });

    return undefined;
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    if (authed) {
      settingsService.getPreferences()
        .then((p) => {
          const stack = stackFromPreference(p.defaultFramework);
          setDefaultStack(stack);
          setNewProjectFramework(stack);
          if (p.theme === 'light' || p.theme === 'dark') setMode(p.theme);
        })
        .catch(() => {});
      settingsService.getMe()
        .then((me) => {
          const type = me.userType || (me.role === 'admin' ? 'admin' : 'user');
          setUserType(type);
          setUserPermissions(me.admin_permissions || []);
          // Redirect admin to settings on first load (not if already navigated)
          if (type === 'admin' && !sessionStorage.getItem('app_view') && !location.pathname.startsWith('/admin')) {
            navigate('/admin/dashboard', { replace: true });
          }
        })
        .catch(() => {});
      setProjectsLoaded(true);
    }
  }, [authed]);

  const styles = getStyles(theme);

  if (!authed) return <Navigate to="/login" replace />;
  if (!projectsLoaded) {
    return (
      <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textMuted, fontSize: 14 }}>
        Loading...
      </div>
    );
  }

  const handleLogout = () => {
    logout();
    setAuthed(false);
    setProjectsLoaded(false);
    sessionStorage.removeItem('app_view');
    sessionStorage.removeItem('app_active_project');
    navigate('/login');
  };

  const navigateTab = (tab) => {
    if (tab === 'settings') {
      // Admin goes to dashboard, regular user goes to profile
      if (userType === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/admin/profile');
      }
      return;
    }
    // If currently on admin route, navigate back to /app
    if (location.pathname.startsWith('/admin')) {
      navigate('/app');
    }
    setNewProjectMode(false);
    setView(tab);
  };

  const resetNewProject = () => {
    setNewProjectMode(false);
    setNewProjectName('');
    setNewProjectDesc('');
    setNewProjectOptions({});
    setNewProjectFramework(defaultStack);
    setNewProjectType('web');
  };

  const handleCreateProject = async (opts = {}) => {
    if (!newProjectName.trim()) {
      toast.error('Please enter a project name');
      return;
    }
    // The prompt is what the project is FOR — a project created without
    // one opens an empty workspace with nothing to generate.
    //
    // Except for the two tools that ARE the input: a design reference or
    // an imported repository is what gets built from, so requiring a
    // typed prompt as well would block them.
    if (!opts.action && !newProjectDesc.trim()) {
      toast.error('Please describe what you want to build');
      return;
    }
    try {
      const res = await authFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newProjectName, description: newProjectDesc, framework: newProjectType === 'mobile' ? 'react-native' : newProjectFramework, project_type: newProjectType }),
      });
      if (res.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
        return;
      }
      if (res.status === 403) {
        const err = await res.json().catch(() => ({}));
        setShowUpgrade(true);
        toast.warning(err.detail || 'Plan limit reached. Please upgrade.');
        return;
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create project');
      }
      const data = await res.json();
      const projectId = data.project_id || data.id;
      if (!projectId) throw new Error('No project ID returned');
      // ONE-SCREEN-CREATE: carry the prompt and any tool request into the
      // workspace, which starts generating immediately and opens straight
      // onto code + preview instead of the separate hero screen.
      const prompt = (newProjectDesc || '').trim();
      const action = opts.action || null;
      resetNewProject();
      setPendingPrompt(prompt ? { text: prompt, ...newProjectOptions } : null);
      setPendingAction(action);
      setActiveProjectId(projectId);
      setView('workspace');
      setTimeout(() => toast.success('Project created successfully!'), 500);
    } catch (e) {
      toast.error('Failed to create project: ' + e.message);
    }
  };

  // The design-reference and import flows POST against a real project,
  // so on the New Project screen they create it first and then open in
  // the workspace. Nothing else about them changes.
  const handleCreateWithReference = () => handleCreateProject({ action: 'reference' });
  const handleCreateWithImport = () => handleCreateProject({ action: 'import' });

  const handleUseTemplate = async (title, prompt) => {
    try {
      const res = await authFetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: title, description: prompt, framework: defaultStack }),
      });
      if (res.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
        return;
      }
      if (!res.ok) throw new Error(`Project creation failed (${res.status})`);
      const data = await res.json();
      const projectId = data.project_id || data.id;
      if (!projectId) throw new Error('No project ID returned');
      const templateType = resolveTemplateType(title);
      if (templateType && hasTemplateFiles(templateType)) {
        await seedProjectTemplate(projectId, templateType, { strict: true });
      }
      setActiveTemplate(templateType ? { type: templateType, name: title } : null);
      setActiveProjectId(projectId);
      setView('workspace');
      setTimeout(() => toast.success(`"${title}" template loaded — customize it with AI prompts!`), 400);
    } catch (e) {
      toast.error('Failed to create project: ' + e.message);
    }
  };

  const openNewProject = (mode) => {
    setNewProjectMode(true);
    setView('projects');
  };

  const startTour = () => { setView('home'); setNewProjectMode(false); setTourStep(0); setTourActive(true); };

  // TourController rendered at top level so it persists when view=workspace
  const tourNode = tourActive ? (
    <TourController
      view={view}
      newProjectMode={newProjectMode}
      activeProjectId={activeProjectId}
      step={tourStep}
      onStep={setTourStep}
      onFinish={() => { onboarding.save('completed'); setTourActive(false); setTourStep(0); }}
      onSkip={() => { onboarding.save('skipped'); setTourActive(false); setTourStep(0); }}
    />
  ) : null;

  if (view === 'workspace' && activeProjectId) {
    return (
      <>
        {tourNode}
        <ProjectWorkspace
        projectId={activeProjectId}
        initialJobId={null}
        initialPrompt={pendingPrompt}
        initialAction={pendingAction}
        onInitialConsumed={() => { setPendingPrompt(null); setPendingAction(null); }}
        templateMeta={activeTemplate}
        onBack={() => {
          setActiveProjectId(null);
          setActiveTemplate(null);
          setView('projects');
        }}
        onOpenSettings={() => setView('settings')}
        />
      </>
    );
  }

  return (
    <div style={styles.app}>
      <div className="ai-workspace-bg" aria-hidden />
      {showUpgrade && <UpgradeModal onClose={() => setShowUpgrade(false)} message="Free plan allows 2 projects. Upgrade for more." />}
      {onboarding.status !== null && (
        <Onboarding
          status={onboarding.status}
          onSave={onboarding.save}
          onStartTour={() => startTour()}
          onSkip={() => {}}
        />
      )}
      {tourNode}
      <style>{`
        /* ─── Premium glass header ─── */
        .app-header-grid {
          padding: 18px 32px !important;
          background: linear-gradient(180deg, rgba(8,11,24,0.92) 0%, rgba(6,9,20,0.86) 100%) !important;
          border-bottom: 1px solid rgba(99,102,241,0.13) !important;
          box-shadow:
            0 1px 0 rgba(255,255,255,0.05) inset,
            0 16px 48px -16px rgba(0,0,0,0.7),
            0 0 0 1px rgba(99,102,241,0.06),
            0 0 80px rgba(99,102,241,0.05);
          backdrop-filter: blur(28px) saturate(1.6);
          -webkit-backdrop-filter: blur(28px) saturate(1.6);
        }
        [data-theme="light"] .app-header-grid {
          background: rgba(255,255,255,0.88) !important;
          border-bottom-color: rgba(99,102,241,0.11) !important;
          box-shadow:
            0 1px 0 rgba(255,255,255,0.98) inset,
            0 8px 32px -12px rgba(99,102,241,0.1),
            0 1px 2px rgba(15,23,42,0.04);
        }

        .app-header-brand {
          display: flex;
          align-items: center;
          gap: 14px;
          min-width: 0;
        }

        /* ─── Brand logo ─── */
        .app-brand-logo {
          flex-shrink: 0;
          width: 40px;
          height: 40px;
        }
        .app-brand-logo__surface {
          position: relative;
          display: block;
          width: 40px;
          height: 40px;
          border-radius: 11px;
          background: rgba(255, 255, 255, 0.04);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(99, 102, 241, 0.32);
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.08),
            0 4px 16px -6px rgba(99, 102, 241, 0.35);
          overflow: hidden;
          transition: box-shadow 0.25s ease, transform 0.25s ease;
        }
        [data-theme="light"] .app-brand-logo__surface {
          background: rgba(255, 255, 255, 0.9);
          border-color: rgba(99, 102, 241, 0.22);
        }
        .app-header-brand:hover .app-brand-logo__surface {
          box-shadow:
            inset 0 1px 0 rgba(255, 255, 255, 0.12),
            0 8px 24px -6px rgba(99, 102, 241, 0.5);
          transform: translateY(-1px);
        }
        .app-brand-logo__shape {
          position: absolute;
          border-radius: 3px;
          background: var(--gradient-primary);
        }
        .app-brand-logo__shape--a { width: 14px; height: 14px; top: 9px; left: 9px; opacity: 0.95; }
        .app-brand-logo__shape--b {
          width: 10px; height: 10px; top: 11px; right: 9px; opacity: 0.65;
          background: linear-gradient(135deg, rgba(139, 92, 246, 0.9), rgba(99, 102, 241, 0.5));
        }
        .app-brand-logo__shape--c { width: 18px; height: 5px; bottom: 9px; left: 11px; border-radius: 2px; opacity: 0.8; }
        /* ─── Brand text ─── */
        .app-brand__text {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }
        .app-brand__name {
          font-size: 15px;
          font-weight: 700;
          letter-spacing: -0.03em;
          line-height: 1.2;
          white-space: nowrap;
          color: var(--text-primary);
        }
        .app-brand__tagline {
          font-size: 11px;
          font-weight: 500;
          color: var(--text-muted);
          letter-spacing: 0.01em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        @media (max-width: 640px) { .app-brand__tagline { display: none; } }

        /* Floating segmented nav capsule */
        .app-header-nav {
          position: relative;
          display: inline-flex;
          align-items: stretch;
          gap: 3px;
          min-width: 580px;
          padding: 7px;
          border-radius: 50px;
          background: rgba(5,7,18,0.62);
          border: 1px solid rgba(99,102,241,0.16);
          box-shadow:
            0 1px 0 rgba(255,255,255,0.05) inset,
            0 10px 36px -14px rgba(0,0,0,0.55),
            0 0 0 1px rgba(99,102,241,0.07),
            0 0 40px rgba(99,102,241,0.06);
          backdrop-filter: blur(24px) saturate(1.4);
          -webkit-backdrop-filter: blur(24px) saturate(1.4);
        }
        [data-theme="light"] .app-header-nav {
          background: rgba(255,255,255,0.82);
          border-color: rgba(99,102,241,0.15);
          box-shadow:
            0 1px 0 rgba(255,255,255,0.98) inset,
            0 6px 24px -10px rgba(99,102,241,0.12),
            0 1px 2px rgba(15,23,42,0.04);
        }

        /* Translucent floating glass active capsule */
        .app-nav-indicator {
          position: absolute;
          top: 7px;
          bottom: 7px;
          left: 0;
          border-radius: 50px;
          background: linear-gradient(
            135deg,
            rgba(99,102,241,0.26) 0%,
            rgba(139,92,246,0.17) 48%,
            rgba(96,165,250,0.11) 100%
          );
          border: 1px solid rgba(165,180,252,0.32);
          box-shadow:
            0 6px 22px -8px rgba(99,102,241,0.42),
            0 1px 0 rgba(255,255,255,0.18) inset,
            inset 0 -1px 0 rgba(99,102,241,0.10),
            0 0 28px rgba(99,102,241,0.15);
          backdrop-filter: blur(16px) saturate(1.4);
          -webkit-backdrop-filter: blur(16px) saturate(1.4);
          will-change: transform, width, opacity;
          transition:
            transform 0.54s cubic-bezier(0.22,1,0.36,1),
            width 0.64s cubic-bezier(0.34,1.06,0.64,1),
            opacity 0.28s ease,
            box-shadow 0.45s ease;
          pointer-events: none;
          z-index: 0;
          backface-visibility: hidden;
        }
        [data-theme="light"] .app-nav-indicator {
          background: linear-gradient(
            135deg,
            rgba(99,102,241,0.13) 0%,
            rgba(139,92,246,0.08) 50%,
            rgba(147,197,253,0.08) 100%
          );
          border-color: rgba(99,102,241,0.22);
          box-shadow:
            0 4px 14px -6px rgba(99,102,241,0.16),
            0 1px 0 rgba(255,255,255,0.95) inset;
        }

        /* Glass reflection highlight */
        .app-nav-indicator::before {
          content: '';
          position: absolute;
          top: 1px;
          left: 14%;
          right: 14%;
          height: 48%;
          border-radius: 50px 50px 65% 65%;
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.2) 0%,
            rgba(255, 255, 255, 0.04) 55%,
            transparent 100%
          );
          pointer-events: none;
        }
        [data-theme="light"] .app-nav-indicator::before {
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0.85) 0%,
            rgba(255, 255, 255, 0.15) 60%,
            transparent 100%
          );
        }

        /* Soft theme glow beneath capsule */
        .app-nav-indicator::after {
          content: '';
          position: absolute;
          inset: -4px -2px;
          border-radius: inherit;
          background: radial-gradient(
            ellipse 90% 80% at 50% 55%,
            rgba(99, 102, 241, 0.14) 0%,
            rgba(139, 92, 246, 0.06) 50%,
            transparent 72%
          );
          z-index: -1;
          opacity: 0.9;
          transition: inherit;
        }
        [data-theme="light"] .app-nav-indicator::after {
          opacity: 0.55;
        }

        .app-nav-tab {
          position: relative;
          z-index: 1;
          flex: 1;
          min-width: 120px;
          padding: 10px 22px;
          min-height: 40px;
          border: none;
          border-radius: 50px;
          background: transparent;
          color: var(--text-secondary);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.012em;
          cursor: pointer;
          white-space: nowrap;
          text-align: center;
          transform: translate3d(0, 0, 0);
          transition:
            color 0.38s cubic-bezier(0.22, 1, 0.36, 1),
            background 0.38s cubic-bezier(0.22, 1, 0.36, 1),
            transform 0.42s cubic-bezier(0.22, 1, 0.36, 1),
            opacity 0.38s ease;
        }
        .app-nav-tab:hover:not(.app-nav-tab--active) {
          color: var(--text-primary);
          background: rgba(99, 102, 241, 0.06);
        }
        [data-theme="light"] .app-nav-tab:hover:not(.app-nav-tab--active) {
          background: rgba(99, 102, 241, 0.05);
        }
        .app-nav-tab:active:not(.app-nav-tab--active) {
          transform: translate3d(0, 0, 0) scale(0.985);
        }
        .app-nav-tab:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px var(--bg-secondary), 0 0 0 4px rgba(99, 102, 241, 0.4);
        }
        .app-nav-tab--active {
          color: var(--text-primary);
        }
        .app-header-nav--indicator-ready .app-nav-tab--active {
          color: rgba(199,210,254,0.97);
          transform: translate3d(0, -1px, 0);
        }
        [data-theme="light"] .app-header-nav--indicator-ready .app-nav-tab--active {
          color: #4f46e5;
        }

        @media (prefers-reduced-motion: reduce) {
          .app-nav-indicator,
          .app-nav-tab {
            transition: none !important;
          }
          .app-header-nav--indicator-ready .app-nav-tab--active {
            transform: none;
          }
        }

        .app-header-theme-toggle {
          width: 36px;
          min-width: 36px;
          height: 36px;
          padding: 0 !important;
          border-radius: 50px !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
          background: rgba(255, 255, 255, 0.04) !important;
          margin-left: 4px;
        }
        [data-theme="light"] .app-header-theme-toggle {
          background: rgba(241, 245, 249, 0.9) !important;
          border-color: rgba(99, 102, 241, 0.15) !important;
        }
        .app-header-theme-toggle:hover {
          border-color: rgba(99, 102, 241, 0.4) !important;
          background: rgba(99, 102, 241, 0.1) !important;
          box-shadow: 0 2px 10px rgba(99, 102, 241, 0.18) !important;
          transform: translateY(-1px);
        }

        /* Actions capsule */
        .app-header-actions {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 5px;
          border-radius: 50px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }
        [data-theme="light"] .app-header-actions {
          background: rgba(241, 245, 249, 0.9);
          border-color: rgba(99, 102, 241, 0.1);
        }

        .app-header-action {
          transition:
            color 0.22s ease,
            background 0.22s ease,
            border-color 0.22s ease,
            box-shadow 0.22s ease,
            transform 0.15s ease !important;
        }
        .app-header-action--icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          min-width: 36px;
          padding: 0 !important;
          border-radius: 50px !important;
        }
        .app-header-action:hover {
          border-color: rgba(99, 102, 241, 0.4) !important;
          color: var(--accent) !important;
          background: rgba(99, 102, 241, 0.1) !important;
          box-shadow: 0 2px 10px rgba(99, 102, 241, 0.15) !important;
        }
        .app-header-action:active {
          transform: scale(0.96);
        }
        .app-header-action:focus-visible {
          outline: none;
          box-shadow: 0 0 0 2px var(--bg-secondary), 0 0 0 4px rgba(99, 102, 241, 0.4) !important;
        }

        .app-header-version {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 6px 10px;
          border-radius: 50px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.03em;
          color: var(--success) !important;
          background: rgba(34, 197, 94, 0.1);
          border: 1px solid rgba(34, 197, 94, 0.22);
        }
        [data-theme="light"] .app-header-version {
          background: rgba(22, 163, 74, 0.08);
          border-color: rgba(22, 163, 74, 0.2);
        }

        /* ─── AI Workspace content background ─── */
        .ai-workspace-bg {
          position: fixed;
          inset: 0;
          pointer-events: none;
          z-index: 0;
          /* Layered: base + radial glows + grid */
          background:
            /* corner glow top-left */
            radial-gradient(ellipse 55% 40% at 8% 12%, rgba(99,102,241,0.09) 0%, transparent 70%),
            /* corner glow bottom-right */
            radial-gradient(ellipse 50% 35% at 92% 88%, rgba(139,92,246,0.07) 0%, transparent 70%),
            /* center ambient */
            radial-gradient(ellipse 80% 60% at 50% 50%, rgba(99,102,241,0.04) 0%, transparent 65%);
          background-size: 100% 100%, 100% 100%, 100% 100%;
        }
        [data-theme="light"] .ai-workspace-bg {
          background:
            radial-gradient(ellipse 55% 40% at 8% 12%, rgba(99,102,241,0.05) 0%, transparent 70%),
            radial-gradient(ellipse 50% 35% at 92% 88%, rgba(139,92,246,0.04) 0%, transparent 70%),
            radial-gradient(ellipse 80% 60% at 50% 50%, rgba(99,102,241,0.025) 0%, transparent 65%);
          background-size: 100% 100%, 100% 100%, 100% 100%;
        }

        @media (max-width: 900px) {
          .app-header-grid {
            grid-template-columns: 1fr !important;
            gap: 14px !important;
            padding: 14px 20px !important;
          }
          .app-header-nav-wrap {
            order: 3;
            width: 100%;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 2px;
          }
          .app-header-nav {
            min-width: max-content;
            width: 100%;
          }
          .app-nav-tab {
            min-width: 96px;
            flex: 0 0 auto;
            padding: 10px 18px;
          }
          .app-header-actions {
            justify-content: flex-start !important;
            width: fit-content;
          }
          .app-header-subtitle {
            white-space: normal !important;
          }
          .app-main-content {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }
        }
      `}</style>

      <header className="app-header-grid" style={styles.header}>
        <Link to="/" className="app-header-brand" style={styles.headerBrandLink}>
          <span className="app-brand-logo" aria-hidden>
            <span className="app-brand-logo__surface">
              <span className="app-brand-logo__shape app-brand-logo__shape--a" />
              <span className="app-brand-logo__shape app-brand-logo__shape--b" />
              <span className="app-brand-logo__shape app-brand-logo__shape--c" />
            </span>
          </span>
          <span className="app-brand__text">
            <span className="app-brand__name">DS CodeXAI</span>
            <span className="app-brand__tagline">Autonomous Software Engineering Platform</span>
          </span>
        </Link>

        <div className="app-header-nav-wrap" style={styles.headerNavWrap}>
          <nav
            ref={navRef}
            className={`app-header-nav${navIndicator.opacity && navIndicator.width > 0 ? ' app-header-nav--indicator-ready' : ''}`}
            style={styles.headerNav}
            aria-label="Main navigation"
          >
            <span
              className="app-nav-indicator"
              aria-hidden
              style={{
                transform: `translate3d(${navIndicator.left}px, 0, 0)`,
                width: navIndicator.width,
                opacity: navIndicator.opacity,
              }}
            />
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                type="button"
                ref={(el) => { tabRefs.current[item.id] = el; }}
                className={`app-nav-tab${activeTab === item.id ? ' app-nav-tab--active' : ''}`}
                onClick={() => navigateTab(item.id)}
                aria-current={activeTab === item.id ? 'page' : undefined}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="app-header-actions" style={styles.headerRight}>
          <HelpButton
            onStartTour={startTour}
            onNavigateProjects={openNewProject}
            onGetView={() => view}
          />
          <span className="app-header-version" style={styles.version}>● v0.3.0</span>
          <InvitesBell onAccepted={(projectId) => {
            setActiveProjectId(projectId);
            setView('workspace');
          }} />
          <button type="button" className="app-header-action" onClick={handleLogout} style={styles.actionBtn}>
            Sign Out
          </button>
        </div>

        <button
          type="button"
          className="app-header-action app-header-action--icon app-header-theme-toggle"
          onClick={toggle}
          style={styles.themeToggleBtn}
          title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label="Toggle theme"
        >
          {mode === 'dark' ? '☀️' : '🌙'}
        </button>
      </header>

      <main className="app-main-content" style={{ ...styles.main, ...(newProjectMode || view === 'projects' || view === 'templates' ? styles.mainProjects : {}), ...(view === 'settings' ? styles.mainAdmin : {}) }}>
        {newProjectMode ? (
          <div style={{ width: '100%', background: 'rgba(15,18,32,0.62)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', border: '1px solid rgba(99,102,241,0.16)', borderRadius: 24, padding: '32px 32px 36px', margin: '28px 0 40px', boxShadow: '0 0 0 1px rgba(99,102,241,0.06), 0 8px 48px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 24, position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 990, width: '100%', margin: '0 auto' }}>
              <h2 style={styles.formTitle}>New Project</h2>
              <p style={styles.formSubtitle}>
                Create a project workspace. You can iterate on it with multiple prompts.
              </p>

              <label style={styles.label}>Project Type</label>
              <div data-tour="project-type" style={{ display: 'flex', gap: 10 }}>
                {[
                  {
                    id: 'web',
                    label: 'Web App',
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="3" width="20" height="14" rx="2" />
                        <path d="M8 21h8M12 17v4" />
                        <path d="M6 8h.01M9 8h6" />
                        <path d="M6 11h12" />
                      </svg>
                    ),
                  },
                  ...( process.env.REACT_APP_HIDE_MOBILE_APP !== 'true' ? [{
                    id: 'mobile',
                    label: 'Mobile App',
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="5" y="2" width="14" height="20" rx="3" />
                        <circle cx="12" cy="18" r="1" fill="currentColor" stroke="none" />
                        <path d="M9 6h6" />
                      </svg>
                    ),
                  }] : []),
                ].map(t => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setNewProjectType(t.id)}
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      background: newProjectType === t.id ? 'rgba(99,102,241,0.15)' : theme.inputBg,
                      border: `1px solid ${newProjectType === t.id ? '#6366f1' : theme.borderLight}`,
                      borderRadius: 8,
                      color: newProjectType === t.id ? '#a5b4fc' : theme.textSecondary,
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: 'pointer',
                      boxShadow: newProjectType === t.id ? '0 0 0 1px #6366f1' : 'none',
                    }}
                  >
                    {t.icon} {t.label}
                  </button>
                ))}
              </div>

              <label style={styles.label}>Project Name</label>
              <input
                data-tour="project-name-input"
                style={styles.input}
                placeholder="e.g. My Todo App, E-Commerce Store, Healthcare Portal"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                autoFocus
              />

              {/* ONE-SCREEN-CREATE: this was a plain textarea, so the
                  prompt tools (import, design reference, hero video,
                  match-my-prompt) only existed on the separate hero
                  screen AFTER the project was created. The composer is
                  shared now, so name + prompt + stack are all decided
                  here and the workspace opens straight onto code and
                  preview. */}
              <label style={styles.label}>What will you build today?</label>
              <PromptComposer
                value={newProjectDesc}
                onChange={setNewProjectDesc}
                onSubmit={handleCreateProject}
                onOpenImport={handleCreateWithImport}
                onChangeOptions={setNewProjectOptions}
                onRequestReference={handleCreateWithReference}
                placeholder="Describe your app…"
                showSubmit={false}
              />

              {newProjectType === 'web' && (
                <>
                  <label style={styles.label}>Tech Stack</label>
                  <StackSelector value={newProjectFramework} onChange={setNewProjectFramework} />
                </>
              )}
              {newProjectType === 'mobile' && (
                <div style={{ padding: '10px 14px', background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, fontSize: 12, color: '#a5b4fc' }}>
                  React Native + Expo Router — generates a complete mobile app with screens, navigation, and components.
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                {/* The primary action lives in the composer's toolbar so
                    Enter-to-create and the button agree. Kept here too
                    for the guided tour anchor and for creating a project
                    with no prompt at all. */}
                <button type="button" data-tour="create-workspace-btn" onClick={handleCreateProject} style={styles.primaryBtn}>
                  Create & Open Workspace
                </button>
                <button type="button" onClick={resetNewProject} style={styles.secondaryBtn}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        ) : view === 'settings' ? (
          <AdminLayout />
        ) : (
          <ProjectList
            section={view === 'projects' ? 'projects' : view === 'templates' ? 'templates' : 'home'}
            onSelectProject={(id) => {
              setActiveTemplate(null);
              setActiveProjectId(id);
              setView('workspace');
            }}
            onNewProject={openNewProject}
            onUseTemplate={handleUseTemplate}
          />
        )}
      </main>
    </div>
  );
}

const getStyles = (theme) => ({
  app: { minHeight: '100vh', background: theme.bg, position: 'relative' },
  header: {
    display: 'grid',
    gridTemplateColumns: 'minmax(220px, 1fr) auto minmax(140px, 1fr) auto',
    alignItems: 'center',
    gap: 24,
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  headerLeft: { minWidth: 0 },
  headerBrandLink: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
    color: 'inherit',
    cursor: 'pointer',
  },
  brandText: { minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 },
  headerNavWrap: { display: 'flex', justifyContent: 'center' },
  headerNav: { display: 'inline-flex', alignItems: 'center' },
  headerRight: { justifySelf: 'end' },
  version: { whiteSpace: 'nowrap' },
  themeToggleBtn: {
    justifySelf: 'end',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    minWidth: 36,
    height: 36,
    padding: 0,
    border: '1px solid transparent',
    borderRadius: 50,
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 16,
  },
  actionBtn: {
    padding: '7px 12px',
    minHeight: 36,
    border: `1px solid transparent`,
    borderRadius: 50,
    background: 'transparent',
    color: theme.textSecondary,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '0.01em',
    cursor: 'pointer',
  },
  main: { width: '100%', padding: '0 24px', position: 'relative', zIndex: 1, boxSizing: 'border-box' },
  mainProjects: { maxWidth: 1100, margin: '0 auto', width: '100%' },
  mainAdmin: { padding: 0, maxWidth: 'none', margin: 0 },
  newProjectForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: 32,
    background: theme.bgSecondary,
    borderRadius: 16,
    border: `1px solid ${theme.border}`,
  },
  formTitle: { fontSize: 22, fontWeight: 700, color: theme.text, marginBottom: 0 },
  formSubtitle: { fontSize: 13, color: theme.textMuted, marginTop: -8, marginBottom: 8 },
  label: { fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: -8 },
  input: {
    padding: '12px 14px',
    background: theme.inputBg,
    border: `1px solid ${theme.borderLight}`,
    borderRadius: 8,
    color: theme.text,
    fontSize: 14,
    outline: 'none',
    width: '100%',
  },
  primaryBtn: {
    padding: '12px 24px',
    background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontWeight: 600,
    cursor: 'pointer',
    fontSize: 14,
  },
  secondaryBtn: {
    padding: '12px 24px',
    background: 'transparent',
    color: theme.textSecondary,
    border: `1px solid ${theme.borderLight}`,
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
  },
});
